// PWA helpers — service worker registration, standalone detection,
// install-prompt capture, and notification permission scaffold.
//
// Kept dependency-free so it can run before React mounts (from index.js).
import { log } from "@/lib/log";

let deferredInstallPrompt = null;
const installListeners = new Set();

/** Register the service worker (production only by default). */
export function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) return;
  // Allow opting in during dev with ?sw=1 — handy for local testing.
  const isDev = process.env.NODE_ENV === "development";
  const force = typeof window !== "undefined" && window.location.search.includes("sw=1");
  if (isDev && !force) return;

  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js", { scope: "/" })
      .then((reg) => {
        // Auto-activate updated SW + reload once on controllerchange to pick up new bundle.
        if (reg.waiting) {
          reg.waiting.postMessage({ type: "SKIP_WAITING" });
        }
        reg.addEventListener("updatefound", () => {
          const nw = reg.installing;
          if (!nw) return;
          nw.addEventListener("statechange", () => {
            if (nw.state === "installed" && navigator.serviceWorker.controller) {
              nw.postMessage({ type: "SKIP_WAITING" });
            }
          });
        });
      })
      .catch((err) => log.warn("[pwa] sw register failed", err));

    let reloaded = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (reloaded) return;
      reloaded = true;
      window.location.reload();
    });
  });
}

/** True when the app is launched as an installed PWA (Android/iOS/desktop). */
export function isStandalone() {
  if (typeof window === "undefined") return false;
  if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
  // iOS Safari sets navigator.standalone.
  if (typeof navigator !== "undefined" && navigator.standalone === true) return true;
  return false;
}

/** Capture beforeinstallprompt so a custom UI can trigger install later. */
export function captureInstallPrompt() {
  if (typeof window === "undefined") return;
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredInstallPrompt = e;
    installListeners.forEach((cb) => { try { cb(true); } catch (e) { log.warn("[pwa] install listener (avail=true) threw:", e); } });
  });
  window.addEventListener("appinstalled", () => {
    deferredInstallPrompt = null;
    installListeners.forEach((cb) => { try { cb(false); } catch (e) { log.warn("[pwa] install listener (avail=false) threw:", e); } });
  });
}

export function hasInstallPrompt() {
  return !!deferredInstallPrompt;
}

export function onInstallPromptChange(cb) {
  installListeners.add(cb);
  return () => installListeners.delete(cb);
}

/** Trigger native install UI. Returns 'accepted' | 'dismissed' | 'unavailable'. */
export async function triggerInstall() {
  if (!deferredInstallPrompt) return "unavailable";
  try {
    deferredInstallPrompt.prompt();
    const choice = await deferredInstallPrompt.userChoice;
    deferredInstallPrompt = null;
    installListeners.forEach((cb) => { try { cb(false); } catch (e) { log.warn("[pwa] install listener after prompt threw:", e); } });
    return choice.outcome || "dismissed";
  } catch (e) {
    log.warn("[pwa] triggerInstall failed:", e);
    return "dismissed";
  }
}

/** True on iOS Safari, where install requires the manual "Add to Home Screen" path. */
export function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const iOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  // iPadOS 13+ identifies as Mac but has touch — also treat as iOS for install UX.
  const iPadOS = ua.includes("Mac") && navigator.maxTouchPoints > 1;
  return iOS || iPadOS;
}

/** Scaffold for future push notifications — request permission, no send yet. */
export async function requestNotificationPermission() {
  if (typeof window === "undefined" || !("Notification" in window)) return "unsupported";
  if (Notification.permission !== "default") return Notification.permission;
  try { return await Notification.requestPermission(); } catch (e) { log.warn("[pwa] requestNotificationPermission failed:", e); return "denied"; }
}
